import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  Alert,
  AlertTitle,
  AlertDescription,
  VStack,
  AlertIcon,
  Image, 
  useDisclosure,
  Tag,
  useToast,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { getStorageImageUrl } from 'utils/imageUrl'
import OptimizedImage from 'components/image/OptimizedImage'

import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { useGetBlogPosts, useDeleteBlogPost } from 'queries/blogPost.query'
import { CreateBlogPostModal } from './CreateBlogPostModal'
import { EditBlogPostModal } from './EditBlogPostModal'
import { DeleteBlogPostModal } from './DeleteBlogPostModal'
import { CustomPagination } from 'views/admin/communities/components/pagination'

const columnHelper = createColumnHelper()

export default function BlogPostTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const {
    isOpen: isDeleteOpen,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure()
  const {
    isOpen: isEditOpen,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure()
  const {
    isOpen: isCreateOpen,
    onOpen: onOpenCreate,
    onClose: onCloseCreate,
  } = useDisclosure()

  const toast = useToast();

  const [selectedPost, setSelectedPost] = React.useState(null)
  const [selectedForDeletion, setSelectedForDeletion] = React.useState(null)

  const { mutate: deleteMutate, isPending: isDeletePending } =
    useDeleteBlogPost()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  })

  const { data, isLoading, isError, refetch } = useGetBlogPosts(
    pagination.current,
    pagination.pageSize,
  )

  const rows = data?.data || []

  const handleEditOpen = post => {
    setSelectedPost(post)
    onOpenEdit()
  }

  const handleSingleDelete = id => {
    setSelectedForDeletion([id])
    onOpenDelete()
  }

  const onDelete = () => {
    deleteMutate(selectedForDeletion, {
      onSuccess: () => {
        onCloseDelete()
        setSelectedForDeletion([])
        refetch()
        toast({
          title: 'Blog Post Deleted.',
          description: 'The blog post has been successfully deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
      onError: err => {
        console.error('Delete failed:', err)
        toast({
          title: 'Failed to Delete Blog Post.',
          description:
            err.response?.data?.message ||
            'There was an error deleting the blog post.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  const columns = [
    columnHelper.accessor('title', {
      id: 'title',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Title
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('excerpt', {
      id: 'excerpt',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Excerpt
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('content', {
      id: 'content',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Content
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm'>
            {info.getValue()?.substring(0, 50)}...
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('cover_image', {
      id: 'cover_image',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Cover Image
        </Text>
      ),
      cell: info => {
        const imageUrl = getStorageImageUrl(info.getValue())
        return (
          <Flex justifyContent='center' alignItems='center'>
            {imageUrl ? (
              <OptimizedImage
                src={imageUrl}
                alt='Cover Image'
                boxSize='50px'
                borderRadius='md'
                objectFit='cover'
              />
            ) : (
              <Text color='gray.400' fontSize='xs'>
                No Image
              </Text>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('author', {
      id: 'author',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Author
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm'>
            {info.getValue() || 'N/A'} {/* Display 'N/A' if null */}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('read_time', {
      id: 'read_time',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Read Time
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm'>
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('date', {
      id: 'date',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Date
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm'>
            {info.getValue()
              ? new Date(info.getValue()).toLocaleDateString()
              : 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Category
        </Text>
      ),
      cell: info => {
        const category = info.getValue()
        let colorScheme
        switch (category) {
          case 'concert':
            colorScheme = 'purple'
            break
          case 'cultural':
            colorScheme = 'orange'
            break
          case 'sports':
            colorScheme = 'green'
            break
          default:
            colorScheme = 'gray'
        }
        return (
          <Flex justifyContent='center' alignItems='center'>
            <Tag size='sm' colorScheme={colorScheme}>
              {category || 'N/A'}
            </Tag>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          color='gray.400'
          fontSize={{ sm: '10px', lg: '12px' }}
          textAlign='center'
        >
          Status
        </Text>
      ),
      cell: info => {
        const status = info.getValue()
        let colorScheme
        switch (status) {
          case 'PUBLISHED':
            colorScheme = 'green'
            break
          case 'DRAFT':
            colorScheme = 'yellow'
            break
          default:
            colorScheme = 'gray'
        }
        return (
          <Flex justifyContent='center' alignItems='center'>
            <Tag size='sm' colorScheme={colorScheme}>
              {status || 'N/A'}
            </Tag>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text color='gray.400' fontSize={{ sm: '10px', lg: '12px' }} textAlign='center'>
          ACTIONS
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' isAttached>
            <Button
              colorScheme='brand'
              onClick={() => handleEditOpen(row.original)}
            >
              <EditIcon />
            </Button>
            <Button
              colorScheme='red'
              onClick={() => handleSingleDelete(row.original.id)}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load blog post data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Blog Posts
        </Text>
        <Button
          leftIcon={<AiOutlinePlus />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={onOpenCreate}
        >
          New Blog Post
        </Button>
      </Flex>
      <Box
        maxH='600px'
        overflowY='auto'
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
      >
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    textAlign='center'
                  >
                    <Flex
                      justifyContent='center'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {data && (
        <CustomPagination
          total={data?.total || 0}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onPageChange={page =>
            setPagination(prev => ({ ...prev, current: page }))
          }
          onPageSizeChange={size =>
            setPagination(prev => ({ ...prev, pageSize: size }))
          }
        />
      )}
      <CreateBlogPostModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      <EditBlogPostModal
        isOpen={isEditOpen}
        onClose={onCloseEdit}
        blogPost={selectedPost}
      />
      <DeleteBlogPostModal
        isOpen={isDeleteOpen}
        onClose={onCloseDelete}
        onDelete={onDelete}
        isLoading={isDeletePending}
      />
    </Card>
  )
}
